'use client';

import { fetchRecommendProducts } from '@/api/product';
import { listByProductIds } from '@/api/public';
import InfiniteScroll from '@/components/InfiniteScroll';
import ProductCard from '@/components/Product/ProductCard';
import { useWhenTokenReady } from '@/hooks/useWhenReady';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { getProductDisplayLabel, getProductImages, getWishNumber } from '@/utils/goods-list-utils';
import { priceInfo } from '@/utils/price';
import { setSession } from '@/utils/tools';
import { useTranslations } from '@/i18n/use-translations';
import { serializeKey } from '@/utils/swr/keys';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import useSWR from 'swr';
import {
  useCartSensorsTracking,
  type CartRecommendTrackingParams,
  type RecommendProduct,
} from '../hooks/useCartSensorsTracking';

type RecommendParams = {
  group_id?: number;
  scene?: 'cart';
  strategy?: number;
  useWaterfall?: boolean;
  waterfall_position?: number;
};

type RecommendationStrategy = 'default' | 'cart' | 'error-page';

interface ResolveRecommendRequestPlanInput {
  cartRecommendSource?: 1 | 2;
  recommendationStrategy?: RecommendationStrategy;
  scene?: RecommendParams['scene'];
  siteName: string;
  waterfallPosition: number;
}

/**
 * 将推荐取值策略与 scene 参数解耦：错误页可沿用购物车取值，但不向接口发送 scene。
 */
export function resolveRecommendRequestPlan({
  cartRecommendSource,
  recommendationStrategy = 'default',
  scene,
  siteName,
  waterfallPosition,
}: ResolveRecommendRequestPlanInput) {
  const usesCartStrategy = recommendationStrategy !== 'default' || scene === 'cart';
  const isWaterfallList = usesCartStrategy ? cartRecommendSource === 1 : siteName === 'us';
  const siteRecommendParams: Record<string, RecommendParams> = {
    us: { group_id: 6, useWaterfall: true, waterfall_position: waterfallPosition },
    ca: { group_id: 6 },
    uk: { group_id: 6 },
    de: { group_id: 6, strategy: 1 },
    fr: { group_id: 6, strategy: 1 },
  };
  const cartRecommendParams: RecommendParams = {
    group_id: 6,
    ...(scene ? { scene } : {}),
    ...(isWaterfallList ? { useWaterfall: true, waterfall_position: waterfallPosition } : {}),
  };

  return {
    isWaterfallList,
    requestParams: usesCartStrategy ? cartRecommendParams : siteRecommendParams[siteName],
  };
}

interface ResolveRecommendResponseListInput {
  data: {
    acc_list?: unknown;
    frame_list?: unknown;
    waterfall_list?: unknown;
  };
  hasGlass: boolean;
  isWaterfallList: boolean;
  recommendationStrategy?: RecommendationStrategy;
  shouldFallbackToProductGroup: boolean;
}

/**
 * 选择购物车推荐列表；仅错误页允许在无 scene 的响应里回退到 waterfall_list。
 */
export function resolveRecommendResponseList({
  data,
  hasGlass,
  isWaterfallList,
  recommendationStrategy = 'default',
  shouldFallbackToProductGroup,
}: ResolveRecommendResponseListInput): unknown {
  let recommendList: unknown;

  if (shouldFallbackToProductGroup) {
    recommendList = hasGlass ? data.acc_list : data.waterfall_list;
  } else if (isWaterfallList) {
    recommendList = data.waterfall_list;
  } else {
    recommendList = hasGlass ? data.acc_list : data.frame_list;
  }

  if (
    recommendationStrategy === 'error-page' &&
    (!Array.isArray(recommendList) || recommendList.length === 0) &&
    Array.isArray(data.waterfall_list)
  ) {
    return data.waterfall_list;
  }

  return recommendList;
}

type RecommendRequest = (params: RecommendParams) => ReturnType<typeof fetchRecommendProducts>;

/** 外部注入埋点（如活动页推荐），未传则使用购物车默认埋点 */
export interface YouMightLikeTrackingHandlers {
  onItemClick: (item: RecommendProduct, index: number) => void;
  onProductInView: (item: RecommendProduct, index: number) => void;
  onProductValidExposure: (item: RecommendProduct, index: number) => void;
  onCollectClick?: (item: RecommendProduct, index: number, collectType?: string) => void;
  onModelExp: () => void;
  collectScene: string;
}

interface YouMightLikeWaterFallProps {
  recommendList: RecommendProduct[];
  deviceType: EPlatform;
  recommendParams?: (waterfallPosition: number) => RecommendParams;
  /** 自定义推荐请求，未传时保持购物车默认推荐接口 */
  recommendRequest?: RecommendRequest;
  /** 自定义标题，未传时使用购物车默认文案 */
  sectionTitle?: string;
  onItemClick?: (item: RecommendProduct) => void;
  onAdvertDataLoaded?: (advertData: any, recommendData: any) => void;
  /** 推荐商品全部加载完成时回调 */
  onAllLoaded?: () => void;
  /** 推荐仍在加载或已有商品时通知父级保留推荐布局 */
  onVisibilityChange?: (visible: boolean) => void;
  /** 自定义埋点，传入后覆盖购物车默认埋点 */
  tracking?: YouMightLikeTrackingHandlers;
  /** PC 端商品网格 className，默认与购物车一致 */
  pcGridClassName?: string;
  /** 登录态变化时只同步收藏态，不重新请求整组推荐 */
  syncWishOnTokenChange?: boolean;
  /** 推荐商品展示上限，未传时保持原瀑布流逻辑 */
  maxItems?: number;
  /** 是否按接口 total 判断加载完成，默认关闭以保持其他页面原逻辑 */
  stopWhenResponseTotalReached?: boolean;
  /** 是否按商品标识去重追加，默认关闭以保持其他页面原逻辑 */
  dedupeProductsBeforeAppend?: boolean;
  /** 可选的 SWR 页面缓存命名空间；未传时保持原有临时状态行为 */
  cacheKey?: string;
  /** 是否等待购物车列表完成首次写入后再初始化推荐 */
  waitForCartData?: boolean;
  /** 推荐场景；购物车传 cart，其他复用场景保持默认逻辑 */
  scene?: RecommendParams['scene'];
  /** 推荐取值策略；错误页使用购物车策略，但不通过该属性发送 scene */
  recommendationStrategy?: RecommendationStrategy;
  /** 商品主图宽度，未传时使用 ProductCard 默认值 */
  frameWidth?: string;
}

interface RecommendProductsCacheState {
  waterfallList: RecommendProduct[];
  allRecommendListLoaded: boolean;
  waterfallPosition: number;
  hasInitialized: boolean;
  /** 商品主图宽度，未传时使用 ProductCard 默认值 */
  frameWidth?: string;
}

export function isWapDevice(deviceType: EPlatform): boolean {
  return deviceType !== EPlatform.WEB;
}

// 自定义Hook：管理推荐商品业务逻辑
function useRecommendProducts(
  initialList: RecommendProduct[] = [],
  onAdvertDataLoaded?: (advertData: any, recommendData: any) => void,
  syncWishOnTokenChange = false,
  recommendParams?: (waterfallPosition: number) => RecommendParams,
  maxItems?: number,
  recommendRequest: RecommendRequest = fetchRecommendProducts,
  stopWhenResponseTotalReached = false,
  dedupeProductsBeforeAppend = false,
  cacheKey?: string,
  waitForCartData = false,
  scene?: RecommendParams['scene'],
  recommendationStrategy: RecommendationStrategy = 'default',
) {
  const hasMaxItems = typeof maxItems === 'number' && maxItems > 0;
  const initialWaterfallList = useMemo(
    () => (hasMaxItems ? initialList.slice(0, maxItems) : initialList),
    [hasMaxItems, initialList, maxItems],
  );
  const swrCacheKey = cacheKey
    ? serializeKey(['local', 'data', 'recommendProducts', cacheKey])
    : null;
  const cacheFallback = useMemo<RecommendProductsCacheState>(
    () => ({
      waterfallList: initialWaterfallList,
      allRecommendListLoaded: false,
      waterfallPosition: 0,
      hasInitialized: false,
    }),
    [initialWaterfallList],
  );
  const { data: cachedState, mutate: mutateCachedState } = useSWR<RecommendProductsCacheState>(
    swrCacheKey,
    null,
    {
      fallbackData: cacheFallback,
      revalidateOnMount: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
    },
  );
  const [waterfallList, setWaterfallList] = useState<RecommendProduct[]>(
    () => cachedState?.waterfallList || initialWaterfallList,
  );
  const [loading, setLoading] = useState(false);
  const [allRecommendListLoaded, setAllRecommendListLoaded] = useState(
    () => cachedState?.allRecommendListLoaded || false,
  );
  const [waterfallPosition, setWaterfallPosition] = useState(
    () => cachedState?.waterfallPosition || 0,
  );
  const [hasInitialized, setHasInitialized] = useState(() => cachedState?.hasInitialized || false);
  const prevTokenRef = useRef<string | undefined>(undefined);

  const persistCache = useCallback(() => {
    if (!swrCacheKey) return;

    void mutateCachedState(
      {
        waterfallList,
        allRecommendListLoaded,
        waterfallPosition,
        hasInitialized,
      },
      { revalidate: false },
    );
  }, [
    allRecommendListLoaded,
    hasInitialized,
    mutateCachedState,
    swrCacheKey,
    waterfallList,
    waterfallPosition,
  ]);

  useEffect(() => {
    persistCache();
  }, [persistCache]);

  // 获取货币信息和用户信息
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const canBuyList = useStore((state) => state.canBuyList);
  const token = useStore((state) => state.token);
  const cartRecommendSource = useStore((state) => state.publicResourceData?.cart_rec_source);
  const canInitializeRecommend = useStore((state) => !waitForCartData || state.cartDataReady);

  // 初始化数据 - 默认首次挂载执行；指定等待购物车时，在购物车首次写入后执行
  useEffect(() => {
    if (!canInitializeRecommend) return;

    // 防止重复初始化
    if (hasInitialized) return;

    // 如果没有初始数据或初始数据为空，才加载推荐数据
    if (!initialList || initialList.length === 0) {
      loadRecommendData();
    }

    setHasInitialized(true);
    // 只允许门禁从 false 变为 true 时补一次初始化，后续仍由 hasInitialized 防重。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canInitializeRecommend]);

  // 加载推荐数据
  const loadRecommendData = async () => {
    if (allRecommendListLoaded || loading) return;

    setLoading(true);
    try {
      const count = await getRecommendList();
      if (count === 0) {
        setAllRecommendListLoaded(true);
      }
    } catch (error) {
      logger.error('Failed to load recommend data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateProductsWishStatus = useCallback(async () => {
    try {
      if (waterfallList.length === 0) return;

      const productIds = waterfallList.map((item) => item.id || 0).filter((id) => id > 0);
      if (productIds.length === 0) return;

      const response = await listByProductIds({ product_ids: productIds.join(',') });
      if (response?.code !== 200 || !Array.isArray(response.data?.list)) return;

      const wishMap = response.data.list.reduce<Record<number, number>>((acc, item) => {
        if (item.id && typeof item.is_wish === 'number') {
          acc[item.id] = item.is_wish;
        }
        return acc;
      }, {});

      setWaterfallList((prevList) =>
        prevList.map((item) => {
          const wishProduct = item as RecommendProduct & { is_wish?: number };
          return {
            ...item,
            is_wish:
              item.id && wishMap[item.id] !== undefined ? wishMap[item.id] : wishProduct.is_wish,
          };
        }),
      );
    } catch (error) {
      logger.error('更新推荐商品收藏状态失败:', error);
    }
  }, [waterfallList]);

  useWhenTokenReady(() => {
    if (!syncWishOnTokenChange) return;

    if (prevTokenRef.current === undefined) {
      prevTokenRef.current = token;
      return;
    }

    const prevToken = prevTokenRef.current;

    if (prevToken && !token) {
      setWaterfallList((prevList) =>
        prevList.map((item) => ({
          ...item,
          is_wish: 2,
        })),
      );
    }

    if (token && prevToken !== token) {
      void updateProductsWishStatus();
    }

    prevTokenRef.current = token;
  }, [syncWishOnTokenChange, token, updateProductsWishStatus]);

  // 获取推荐商品列表
  const getRecommendList = async (): Promise<number> => {
    const { isWaterfallList, requestParams: defaultRequestParams } = resolveRecommendRequestPlan({
      cartRecommendSource,
      recommendationStrategy,
      scene,
      siteName: env.NEXT_PUBLIC_SITE_NAME,
      waterfallPosition,
    });

    try {
      const requestParams = recommendParams?.(waterfallPosition) ?? defaultRequestParams;
      const response = await recommendRequest(requestParams);
      if (response.code === 200 && response.data) {
        // 来源由 global init 前置返回：1 算法瀑布流，2 商品分组。
        const hasGlass = canBuyList.some((item) =>
          item.carts.some((cart) => Number(cart.product.is_glass) === 1),
        );
        // 购物车算法推荐异常时会返回字符串哨兵 '-1'，不要扩散到共享接口类型。
        const newPosition: unknown = response.data.waterfall_position;
        const shouldFallbackToProductGroup = newPosition === '-1';
        const waterfall_list = resolveRecommendResponseList({
          data: response.data,
          hasGlass,
          isWaterfallList,
          recommendationStrategy,
          shouldFallbackToProductGroup,
        });
        const {
          cart_settlement_pc_image,
          cart_settlement_wap_image,
          cart_settlement_jump_target_type,
          cart_settlement_jump_target,
          cart_rec_frame_group_id,
          cart_settlement_product_group,
          cart_settlement_sku,
        } = response.data;
        const responseData = response.data as typeof response.data & { total?: number | string };
        const responseTotal = Number(responseData.total);
        const hasResponseTotal =
          stopWhenResponseTotalReached && Number.isFinite(responseTotal) && responseTotal >= 0;

        // 首次加载时传递广告位数据给父组件
        if (waterfallPosition === 0 && onAdvertDataLoaded) {
          const advertData = {
            cart_settlement_pc_image: cart_settlement_pc_image || '',
            cart_settlement_wap_image: cart_settlement_wap_image || '',
            cart_settlement_jump_target_type: cart_settlement_jump_target_type || '',
            cart_settlement_jump_target: cart_settlement_jump_target || '',
            cart_settlement_product_group: cart_settlement_product_group || {},
            cart_settlement_sku: cart_settlement_sku || {},
          };
          const recommendData = {
            cart_rec_frame_group_id: cart_rec_frame_group_id || '',
            waterfall_list: [],
          };
          onAdvertDataLoaded(advertData, recommendData);
        }

        // 确保waterfall_list是有效的商品数组
        const validProducts: RecommendProduct[] = Array.isArray(waterfall_list)
          ? ((waterfall_list as any[]).filter(
              (item: any) => item && typeof item === 'object' && (item.name || item.short_name),
            ) as RecommendProduct[])
          : [];

        if (validProducts.length > 0) {
          if (!hasMaxItems) {
            // 保持购物车、订阅成功页等既有调用路径的原始分页与追加行为。
            if (waterfallPosition === 0) {
              setWaterfallList(validProducts);
            } else {
              setWaterfallList((prev) => [...prev, ...validProducts]);
            }
            if (typeof newPosition === 'number') {
              setWaterfallPosition(newPosition);
            }
            if (!isWaterfallList || shouldFallbackToProductGroup) {
              setAllRecommendListLoaded(true);
            }
            return validProducts.length;
          }

          const baseList = waterfallPosition === 0 ? [] : waterfallList;
          const remainingCount = Math.max(maxItems - baseList.length, 0);
          const appendProducts = dedupeProductsBeforeAppend
            ? (() => {
                const productKeys = new Set(
                  baseList
                    .map((baseItem) => baseItem.id || baseItem.shop_sku || baseItem.business_sku)
                    .filter(Boolean),
                );

                return validProducts.filter((item) => {
                  const productKey = item.id || item.shop_sku || item.business_sku;
                  if (!productKey) return true;
                  if (productKeys.has(productKey)) return false;
                  productKeys.add(productKey);
                  return true;
                });
              })()
            : validProducts;
          const appendList = appendProducts.slice(0, remainingCount);
          const nextList = baseList.concat(appendList);
          const reachedLimit = nextList.length >= maxItems;
          const reachedResponseTotal = hasResponseTotal && nextList.length >= responseTotal;

          setWaterfallList(nextList);
          setWaterfallPosition(
            typeof newPosition === 'number'
              ? newPosition
              : waterfallPosition + validProducts.length,
          );
          if (
            reachedLimit ||
            reachedResponseTotal ||
            !isWaterfallList ||
            shouldFallbackToProductGroup
          ) {
            setAllRecommendListLoaded(true);
          }
          return appendList.length;
        } else {
          setAllRecommendListLoaded(true);
          return 0;
        }
      }
    } catch (error) {
      logger.error('Failed to fetch recommend products:', error);
      setAllRecommendListLoaded(true);
    }
    return 0;
  };

  // 构建商品的神策埋点数据
  const getProductSensorsData = (
    item: RecommendProduct,
    index: number,
  ): CartRecommendTrackingParams => {
    const currencyName = currency?.name || env.NEXT_PUBLIC_DEFAULT_CURRENCY;

    // 获取商品价格信息 - 参考tracking.ts的实现
    const currentPriceInfo = priceInfo(userInfo, item, currency);
    const basePriceInfo = priceInfo(userInfo, item, {
      name: env.NEXT_PUBLIC_DEFAULT_CURRENCY || currency?.name || env.NEXT_PUBLIC_DEFAULT_CURRENCY,
    });

    // 获取商品图片信息 - 参考getGoodsListMktParams
    const { productImage } = getProductImages(item as any);

    return {
      product_index: index,
      shop_sku: item.shop_sku || '',
      business_sku: item.business_sku || '',
      product_sku: item.shop_sku || '', // 使用shop_sku作为product_sku
      product_id: String(item.id || ''),
      product_price: currentPriceInfo.price || '0',
      current_product_price: currentPriceInfo.sell_price || '0',
      base_current_product_price: basePriceInfo.sell_price || '0',
      currency: currencyName,
      recall_group_type: item.recall_group_type || '',
      fine_ranking_type: item.fine_ranking_type || '',
      recommend_result_type: item.recommend_result_type || '',
      product_display_label: getProductDisplayLabel(item as any, 1), // 参考getGoodsListMktParams调用方法
      product_collect_num: Number(getWishNumber(item as any)),
      product_rating: parseFloat((item as any).spu_review_stars || '0'),
      display_color_num: (item as any).correlation?.length || 0,
      first_picture_url: productImage.src,
      product_class_name: (item as any).products_category || '',
    };
  };

  return {
    waterfallList,
    loading,
    allRecommendListLoaded,
    loadRecommendData,
    getProductSensorsData,
    persistCache,
  };
}

// 主组件
export function YouMightLikeWaterFall({
  recommendList,
  deviceType,
  sectionTitle,
  recommendParams,
  recommendRequest,
  onItemClick,
  onAdvertDataLoaded,
  onAllLoaded,
  onVisibilityChange,
  tracking,
  pcGridClassName,
  syncWishOnTokenChange = false,
  maxItems,
  stopWhenResponseTotalReached = false,
  dedupeProductsBeforeAppend = false,
  cacheKey,
  waitForCartData = false,
  scene,
  recommendationStrategy = 'default',
  frameWidth,
}: YouMightLikeWaterFallProps) {
  const t = useTranslations('cart');
  const isWAP = isWapDevice(deviceType);

  // 使用购物车神策埋点Hook（外部未注入 tracking 时）
  const {
    trackCartPageYouAlsoLikeMktClick,
    trackCartPageYouAlsoLikeMktValidExp,
    trackCartPageYouAlsoLikeModelExp,
  } = useCartSensorsTracking();

  const {
    waterfallList,
    loading,
    allRecommendListLoaded,
    loadRecommendData,
    getProductSensorsData,
    persistCache,
  } = useRecommendProducts(
    recommendList,
    onAdvertDataLoaded,
    syncWishOnTokenChange,
    recommendParams,
    maxItems,
    recommendRequest,
    stopWhenResponseTotalReached,
    dedupeProductsBeforeAppend,
    cacheKey,
    waitForCartData,
    scene,
    recommendationStrategy,
  );

  const title = sectionTitle || t('youMayAlsoLike.title');

  // 标题曝光检测 - 用于触发模块曝光埋点
  const [titleRef2, titleInView] = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  // 标题曝光时触发模块曝光埋点
  useEffect(() => {
    if (titleInView && waterfallList.length > 0) {
      if (tracking) {
        tracking.onModelExp();
      } else {
        trackCartPageYouAlsoLikeModelExp();
      }
    }
  }, [titleInView, waterfallList.length, tracking, trackCartPageYouAlsoLikeModelExp]);

  useEffect(() => {
    if (allRecommendListLoaded) {
      onAllLoaded?.();
    }
  }, [allRecommendListLoaded, onAllLoaded]);

  useEffect(() => {
    onVisibilityChange?.(waterfallList.length > 0 || !allRecommendListLoaded);
  }, [allRecommendListLoaded, onVisibilityChange, waterfallList.length]);

  // 如果没有数据且加载完成，不显示组件
  if (waterfallList.length === 0 && allRecommendListLoaded) {
    return null;
  }

  // 处理商品点击
  const handleItemClick = (item: RecommendProduct, index: number) => {
    persistCache();

    if (tracking) {
      tracking.onItemClick(item, index);
    } else {
      const sensorsData = getProductSensorsData(item, index);
      trackCartPageYouAlsoLikeMktClick(sensorsData, item);
      setSession('zl_goodsDetailEnterPageScene', '购物车页猜你喜欢商品坑位');
    }
    if (onItemClick) {
      onItemClick(item);
    }
  };

  // 处理商品有效曝光
  const handleProductValidInView = (item: RecommendProduct, index: number) => {
    if (tracking) {
      tracking.onProductValidExposure(item, index);
      return;
    }
    const sensorsData = getProductSensorsData(item, index);
    trackCartPageYouAlsoLikeMktValidExp(sensorsData, item);
  };

  const collectScene = tracking?.collectScene ?? '购物车你也喜欢推荐商品';
  const pcProductGridClass =
    pcGridClassName ??
    'grid gap-x-[16px] gap-y-[24px] 2xl:grid-cols-4 xl:grid-cols-3 lg:grid-cols-2 sm:grid-cols-1 pb-[64px]';

  return (
    <div
      className={`bg-white font-regular ${isWAP ? 'mx-auto pt-[24px] px-[8px]' : 'mx-auto pt-[40px] px-[48px]'} pb-[60px]`}
    >
      {/* 标题 */}
      <div
        ref={titleRef2}
        className={`font-bb text-center ${isWAP ? 'text-[20px] leading-[24px] mb-[16px]' : 'text-[32px] leading-[40px] mb-[32px]'}`}
      >
        {title}
      </div>

      {/* 商品网格 */}
      <div
        className={`grid ${isWAP ? 'grid-cols-2 gap-x-[8px] gap-y-[40px] relative' : pcProductGridClass}`}
      >
        {waterfallList.map((item, index) => {
          // 为ProductCard转换产品数据格式
          const productForCard = {
            ...item,
            name: item.name || item.short_name || item.shop_sku || '', // ProductCard需要name属性
          };

          return (
            <div key={`${item.id}-${index}`} className="bg-none">
              <ProductCard
                product={productForCard}
                index={index}
                showTryOn={false}
                handelClick={() => handleItemClick(item, index)}
                onValidExposure={() => handleProductValidInView(item, index)}
                wished={() => tracking?.onCollectClick?.(item, index, '收藏')}
                removed={() => tracking?.onCollectClick?.(item, index, '取消收藏')}
                collectScene={collectScene}
                className={isWAP ? 'bg-none' : 'h-full bg-none'}
                imageClassName={
                  [
                    isWAP && 'h-[176px] rounded-[12px]',
                    Number(productForCard.is_glass) !== 1 &&
                      frameWidth &&
                      '[&>a>div>img]:!h-full [&>a>div>img]:!max-h-none',
                  ]
                    .filter(Boolean)
                    .join(' ') || undefined
                }
                frameWidth={Number(productForCard.is_glass) === 1 ? undefined : frameWidth}
                showCorrelation
              />
            </div>
          );
        })}
      </div>

      {/* 无限滚动加载 */}
      {!allRecommendListLoaded && (
        <InfiniteScroll
          loading={loading}
          onReachBottom={loadRecommendData}
          page={0}
          total={1}
          limit={1}
          className={isWAP ? 'pt-[40px]' : 'h-[64px] flex items-center justify-center'}
        />
      )}

      {/* 结束状态 */}
      {waterfallList.length > 0 && allRecommendListLoaded && (
        <div className="flex items-center justify-center py-[40px]">
          <div className="w-[24px] h-[1px] bg-auxiliary-dark-gray mr-[6px]" />
          <span className="text-[12px] text-auxiliary-dark-gray">{t('noMore')}</span>
          <div className="w-[24px] h-[1px] bg-auxiliary-dark-gray ml-[6px]" />
        </div>
      )}
    </div>
  );
}

export default YouMightLikeWaterFall;
